/**
 * Schedule Editor Component
 * Manage weekly schedule for staff members
 */

import { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

const DAYS_OF_WEEK = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 0, label: 'Sunday' }
];

export default function ScheduleEditor({ staffId, staffName, onSubmit, onClose }) {
  const [schedule, setSchedule] = useState(
    DAYS_OF_WEEK.map(day => ({
      dayOfWeek: day.value,
      openTime: '09:00',
      closeTime: '17:00',
      isAvailable: true
    }))
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleToggleDay = (dayValue) => {
    setSchedule(prev =>
      prev.map(item =>
        item.dayOfWeek === dayValue
          ? { ...item, isAvailable: !item.isAvailable }
          : item
      )
    );
  };

  const handleTimeChange = (dayValue, field, value) => {
    setSchedule(prev =>
      prev.map(item =>
        item.dayOfWeek === dayValue
          ? { ...item, [field]: value }
          : item
      )
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    for (const day of schedule) {
      if (day.isAvailable) {
        if (!day.openTime || !day.closeTime) {
          setError('Please set open and close times for all available days');
          return;
        }
        if (day.openTime >= day.closeTime) {
          setError('Close time must be after open time');
          return;
        }
      }
    }

    setLoading(true);
    try {
      await onSubmit(staffId, schedule);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save schedule');
      setLoading(false);
    }
  };

  const handleApplyToAll = () => {
    const firstDay = schedule.find(s => s.isAvailable);
    if (!firstDay) return;

    setSchedule(prev =>
      prev.map(item => ({
        ...item,
        openTime: firstDay.openTime,
        closeTime: firstDay.closeTime
      }))
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Manage Schedule
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Set weekly hours for {staffName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <button
              type="button"
              onClick={handleApplyToAll}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Apply first available day's hours to all days
            </button>
          </div>

          <div className="space-y-3 mb-6 max-h-96 overflow-y-auto">
            {DAYS_OF_WEEK.map(day => {
              const daySchedule = schedule.find(s => s.dayOfWeek === day.value);
              return (
                <div
                  key={day.value}
                  className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center w-32">
                    <input
                      type="checkbox"
                      id={`day-${day.value}`}
                      checked={daySchedule.isAvailable}
                      onChange={() => handleToggleDay(day.value)}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <label
                      htmlFor={`day-${day.value}`}
                      className="ml-2 text-sm font-medium text-gray-900"
                    >
                      {day.label}
                    </label>
                  </div>

                  {daySchedule.isAvailable ? (
                    <div className="flex items-center space-x-2 flex-1">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          Open
                        </label>
                        <input
                          type="time"
                          value={daySchedule.openTime}
                          onChange={(e) =>
                            handleTimeChange(day.value, 'open_time', e.target.value)
                          }
                          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                      <span className="text-gray-400 pt-5">—</span>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          Close
                        </label>
                        <input
                          type="time"
                          value={daySchedule.closeTime}
                          onChange={(e) =>
                            handleTimeChange(day.value, 'close_time', e.target.value)
                          }
                          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="flex-1 text-sm text-gray-400">
                      Not available
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Schedule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
